// src/pages/LabReferral.jsx
import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import AppConst from "../apis/appconst";
import { toast } from "react-toastify";
import DataTable from "react-data-table-component";
import Select from "react-select";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

const LabReferral = () => {
  const [activeSubTab, setActiveSubTab] = useState("all");

  const getCurrentDate = () => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  };

  const [formData, setFormData] = useState({
    search_key: "",
    from: getCurrentDate(),
    to: getCurrentDate(),
    lab_reg_referral_id: "",
    lab_reg_referral: "",
    department_filter: "all",
    records: [],
  });

  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(false);

  // doctor payment modal states
  const [payModalOpen, setPayModalOpen] = useState(false);
  const [payRow, setPayRow] = useState(null);
  const [payAmount, setPayAmount] = useState("");
  const [payProcessing, setPayProcessing] = useState(false);
  const [payError, setPayError] = useState("");

  // ----- helpers -----
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const d = new Date(dateString);
    if (Number.isNaN(d.getTime())) return "";
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const getDoctorNumbers = (row) => {
    const commission = parseFloat(row.lab_reg_commission || 0);
    // support both lab_reg_commission_paid column OR computed paid property returned from backend
    const paid =
      parseFloat(row.lab_reg_commission_paid || 0) ||
      parseFloat(row.doctor_commission_paid || 0) ||
      0;
    const due = Math.max(commission - paid, 0);
    return { commission, paid, due };
  };

  const getDoctorStatusInfo = (row) => {
    const { commission, paid, due } = getDoctorNumbers(row);
    if (commission <= 0) return { status: "N/A", className: "bg-secondary" };
    if (paid <= 0) return { status: "Not Paid", className: "bg-danger" };
    if (due <= 0.01) return { status: "Paid", className: "bg-success" };
    return { status: "Partial", className: "bg-warning text-dark" };
  };

  // ---------- API calls ----------
  const fetchDoctors = async () => {
    try {
      const fd = new FormData();
      fd.append("type", "doctor");
      fd.append("page_no", "none");
      const response = await axios.post(AppConst.BASE_URL + AppConst.getUserListUrl, fd);
      if (response?.data?.status && Array.isArray(response.data.records)) {
        setDoctors(response.data.records);
      } else {
        setDoctors([]);
      }
    } catch (e) {
      console.error("fetchDoctors", e);
      setDoctors([]);
    }
  };

  const fetchLabReferrals = async () => {
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("search_key", formData.search_key || "");
      if (formData.from) fd.append("from", `${formData.from} 00:00:00`);
      if (formData.to) fd.append("to", `${formData.to} 23:59:59`);
      if (formData.lab_reg_referral_id) fd.append("lab_reg_referral_id", String(formData.lab_reg_referral_id));
      if (formData.lab_reg_referral) fd.append("lab_reg_referral", formData.lab_reg_referral);
      fd.append("department_filter", formData.department_filter || "all");
      fd.append("page_no", "none");

      const response = await axios.post(
        AppConst.BASE_URL + AppConst.getDoctorReferralLabUrl,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      if (response.data?.status) {
        setFormData((prev) => ({ ...prev, records: response.data.records || [] }));
      } else {
        toast.error(response.data?.message || "Failed to fetch data");
        setFormData((prev) => ({ ...prev, records: [] }));
      }
    } catch (error) {
      console.error("fetchLabReferrals", error);
      toast.error("Failed to fetch lab referrals: " + (error.response?.data?.message || error.message));
      setFormData((prev) => ({ ...prev, records: [] }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDoctors();
  }, []);

  useEffect(() => {
    setFormData((prev) => ({ ...prev, department_filter: activeSubTab }));
  }, [activeSubTab]);

  useEffect(() => {
    fetchLabReferrals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formData.department_filter,
    formData.from,
    formData.to,
    formData.lab_reg_referral_id,
    formData.lab_reg_referral,
    formData.search_key,
  ]);

  // ----- handlers -----
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const doctorOptions = useMemo(() => {
    return (doctors || []).map((d) => ({
      value: d.user_id,
      label: d.fname || d.name || String(d.user_id),
    }));
  }, [doctors]);

  const selectedDoctor = useMemo(() => {
    if (!formData.lab_reg_referral_id) return null;
    return doctorOptions.find((opt) => String(opt.value) === String(formData.lab_reg_referral_id)) || null;
  }, [formData.lab_reg_referral_id, doctorOptions]);

  const handleDoctorChange = (selectedOption) => {
    if (!selectedOption) {
      setFormData((prev) => ({ ...prev, lab_reg_referral_id: "", lab_reg_referral: "" }));
    } else {
      setFormData((prev) => ({ ...prev, lab_reg_referral_id: String(selectedOption.value), lab_reg_referral: selectedOption.label }));
    }
  };

  const handleReset = () => {
    setFormData((prev) => ({
      ...prev,
      search_key: "",
      from: getCurrentDate(),
      to: getCurrentDate(),
      lab_reg_referral_id: "",
      lab_reg_referral: "",
    }));
  };

  // ----- pay modal functions -----
  const openPayModal = (row) => {
    const { due } = getDoctorNumbers(row);
    setPayRow(row);
    setPayAmount(due > 0 ? due.toFixed(2) : "");
    setPayError("");
    setPayModalOpen(true);
  };

  const closePayModal = () => {
    setPayModalOpen(false);
    setPayRow(null);
    setPayAmount("");
    setPayError("");
  };

  const handlePaySubmit = async (e) => {
    e.preventDefault();
    if (!payRow) return;
    const { commission, paid, due } = getDoctorNumbers(payRow);
    const amt = parseFloat(payAmount || 0);

    if (isNaN(amt) || amt <= 0) {
      setPayError("Please enter a valid amount.");
      return;
    }
    if (amt > due + 0.01) {
      setPayError("Amount cannot be more than due.");
      return;
    }

    try {
      setPayProcessing(true);
      setPayError("");

      const fd = new FormData();
      fd.append("lab_reg_id", payRow.lab_reg_id);
      fd.append("pay_amount", amt.toFixed(2));
      fd.append("note", `Doctor commission payment via UI`);

      const res = await axios.post(AppConst.BASE_URL + AppConst.payDoctorCommissionUrl, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (res.data?.status) {
        toast.success(res.data.message || "Doctor payment saved.");
        closePayModal();
        fetchLabReferrals();
      } else {
        setPayError(res.data?.message || "Failed to save payment.");
      }
    } catch (err) {
      console.error("pay doctor error", err);
      setPayError(err.response?.data?.message || err.message || "Failed to save payment.");
    } finally {
      setPayProcessing(false);
    }
  };

  // ----- totals including doctor commission -----
  const totals = useMemo(() => {
    const recs = formData.records || [];
    let totalAmount = 0;
    let totalPaid = 0;
    let totalCommission = 0;
    let totalDocPaid = 0;
    let totalDocDue = 0;

    recs.forEach((r) => {
      totalAmount += parseFloat(r.department_total || 0);
      totalPaid += parseFloat(r.department_paid || 0);

      const { commission, paid, due } = getDoctorNumbers(r);
      totalCommission += commission;
      totalDocPaid += paid;
      totalDocDue += due;
    });

    return {
      totalAmount: totalAmount.toFixed(2),
      totalPaid: totalPaid.toFixed(2),
      totalCommission: totalCommission.toFixed(2),
      totalDocPaid: totalDocPaid.toFixed(2),
      totalDocDue: totalDocDue.toFixed(2),
    };
  }, [formData.records, activeSubTab]);

  // ----- columns -----
  const columns = useMemo(() => [
    {
      name: "SL No.",
      selector: (row, index) => index + 1,
      width: "80px",
    },
    {
      name: "Date",
      selector: (row) => {
        const dt = new Date(row.lab_reg_datetime);
        return Number.isNaN(dt.getTime()) ? 0 : dt.getTime();
      },
      sortable: true,
      width: "130px",
      cell: (row) => <span>{formatDate(row.lab_reg_datetime)}</span>,
    },
    {
      name: "UHID",
      selector: (row) => row.lab_reg_uhid || "N/A",
      sortable: true,
      wrap: true,
      width: "140px",
    },
    {
      name: "Patient Name",
      selector: (row) => row.lab_reg_name || "",
      sortable: true,
      wrap: true,
      cell: (row) => <div style={{ whiteSpace: "normal" }}>{row.lab_reg_name || ""}</div>,
      width: "200px",
    },
    {
      name: "Tests",
      selector: (row) => row.displayTestNames || "N/A",
      wrap: true,
      grow: 2,
      cell: (row) => (
        <div style={{ whiteSpace: "normal", wordBreak: "break-word", maxWidth: "260px", fontSize: "0.9em" }}>
          {row.displayTestNames || "N/A"}
        </div>
      ),
      width: "240px",
    },
    {
      name: "Doctor Name",
      selector: (row) => row.doctor_name || row.lab_reg_referral || "N/A",
      sortable: true,
      wrap: true,
      width: "220px",
      cell: (row) => <div style={{ whiteSpace: "normal" }}>{row.doctor_name || row.lab_reg_referral || "N/A"}</div>,
    },
    {
      name: "Total Amount",
      selector: (row) => parseFloat(row.department_total || 0),
      sortable: true,
      right: true,
      cell: (row) => <span>₹{parseFloat(row.department_total || 0).toFixed(2)}</span>,
      width: "140px",
    },
    {
      name: "Total Paid",
      selector: (row) => parseFloat(row.department_paid || 0),
      sortable: true,
      right: true,
      cell: (row) => <span>₹{parseFloat(row.department_paid || 0).toFixed(2)}</span>,
      width: "140px",
    },
    {
      name: "Doctor Comm.",
      selector: (row) => parseFloat(row.lab_reg_commission || 0),
      sortable: true,
      right: true,
      cell: (row) => {
        const { commission } = getDoctorNumbers(row);
        return <span className="fw-bold text-success">₹{commission.toFixed(2)}</span>;
      },
      width: "140px",
    },
    {
      name: "Paid",
      selector: (row) => parseFloat(row.lab_reg_commission_paid || 0) || parseFloat(row.doctor_commission_paid || 0),
      sortable: true,
      right: true,
      cell: (row) => {
        const { paid } = getDoctorNumbers(row);
        return <span>₹{paid.toFixed(2)}</span>;
      },
      width: "120px",
    },
    {
      name: "Due",
      selector: (row) => {
        const { due } = getDoctorNumbers(row);
        return due;
      },
      sortable: true,
      right: true,
      cell: (row) => {
        const { due } = getDoctorNumbers(row);
        return <span className={due > 0 ? "text-danger fw-bold" : ""}>₹{due.toFixed(2)}</span>;
      },
      width: "120px",
    },
    {
      name: "Status",
      cell: (row) => {
        const info = getDoctorStatusInfo(row);
        return <span className={`badge rounded-pill ${info.className}`}>{info.status}</span>;
      },
      width: "110px",
    },
    {
      name: "Action",
      cell: (row) => {
        const { due } = getDoctorNumbers(row);
        const disabled = due <= 0.01;
        return (
          <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => openPayModal(row)} disabled={disabled}>
            {disabled ? "Paid" : "Pay"}
          </button>
        );
      },
      width: "100px",
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
  ], [activeSubTab]);

  const customStyles = {
    headCells: {
      style: {
        backgroundColor: "#f1f5fb",
        color: "#405189",
        fontSize: "14px",
        fontWeight: 600,
      },
    },
    rows: { style: { fontSize: "14px", minHeight: "48px" } },
    cells: { style: { paddingTop: "8px", paddingBottom: "8px", paddingLeft: "10px", paddingRight: "10px" } },
  };

  const reactSelectStyles = {
    control: (base) => ({ ...base, minHeight: "38px", fontSize: "14px" }),
    option: (base) => ({ ...base, fontSize: "14px" }),
  };

  // ----- Export PDF (keeps doctor commission column) -----
  const handleExportPDF = () => {
    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
    const pageWidth = doc.internal.pageSize.getWidth();
    doc.setFontSize(16);
    doc.setFont(undefined, "bold");
    doc.text("Lab Referral Report", pageWidth / 2, 15, { align: "center" });
    doc.setFontSize(10);
    doc.setFont(undefined, "normal");
    const subtitle = `Department: ${activeSubTab.toUpperCase()} | Period: ${formatDate(formData.from)} to ${formatDate(formData.to)}`;
    doc.text(subtitle, pageWidth / 2, 22, { align: "center" });

    const tableData = formData.records.map((row, index) => {
      const { commission, paid, due } = getDoctorNumbers(row);
      return [
        index + 1,
        formatDate(row.lab_reg_datetime),
        row.lab_reg_uhid || "N/A",
        row.lab_reg_name || "",
        row.displayTestNames || "N/A",
        row.doctor_name || row.lab_reg_referral || "N/A",
        parseFloat(row.department_total || 0).toFixed(2),
        parseFloat(row.department_paid || 0).toFixed(2),
        commission.toFixed(2),
        paid.toFixed(2),
        due.toFixed(2),
      ];
    });

    tableData.push(["", "", "", "", "TOTAL", "", totals.totalAmount, totals.totalPaid, totals.totalCommission, totals.totalDocPaid, totals.totalDocDue]);

    autoTable(doc, {
      startY: 28,
      head: [["SL","Date","UHID","Patient","Tests","Doctor","Amount","Paid","Doc Comm.","Doc Paid","Doc Due"]],
      body: tableData,
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [64,81,137], textColor: 255, fontStyle: "bold" },
      columnStyles: {
        0:{cellWidth:10},1:{cellWidth:22},2:{cellWidth:25},3:{cellWidth:25},4:{cellWidth:45},5:{cellWidth:35},
        6:{cellWidth:20,halign:"right"},7:{cellWidth:25,halign:"right"},8:{cellWidth:25,halign:"right"},9:{cellWidth:25,halign:"right"},10:{cellWidth:25,halign:"right"}
      },
      didParseCell:(data)=> {
        if (data.row.index === tableData.length - 1) {
          data.cell.styles.fontStyle = "bold";
          data.cell.styles.fillColor = [241,245,251];
        }
      }
    });

    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.text(`Page ${i} of ${pageCount}`, pageWidth / 2, doc.internal.pageSize.getHeight() - 10, { align: "center" });
      doc.text(`Generated on: ${new Date().toLocaleString()}`, 10, doc.internal.pageSize.getHeight() - 10);
    }

    doc.save(`Lab_Referral_${activeSubTab}_${getCurrentDate()}.pdf`);
  };

  // ----- UI -----
  return (
    <div>
      <ul className="nav nav-pills nav-customs nav-primary mb-3" role="tablist">
        <li className="nav-item"><a className={`nav-link ${activeSubTab === "all" ? "active" : ""}`} onClick={() => setActiveSubTab("all")} href="#">All</a></li>
        <li className="nav-item"><a className={`nav-link ${activeSubTab === "pathology" ? "active" : ""}`} onClick={() => setActiveSubTab("pathology")} href="#">Pathology</a></li>
        <li className="nav-item"><a className={`nav-link ${activeSubTab === "radiology" ? "active" : ""}`} onClick={() => setActiveSubTab("radiology")} href="#">Radiology</a></li>
      </ul>

      <div className="card-body border border-dashed border-end-0 border-start-0">
        <div className="row g-3 align-items-end">
          <div className="col-auto">
            <label className="form-label mb-1">From</label>
            <input type="date" className="form-control" name="from" onChange={handleChange} value={formData.from} style={{ width: "150px" }} />
          </div>

          <div className="col-auto">
            <label className="form-label mb-1">To</label>
            <input type="date" className="form-control" name="to" onChange={handleChange} value={formData.to} style={{ width: "150px" }} />
          </div>

          <div className="col-auto" style={{ minWidth: "220px" }}>
            <label className="form-label mb-1">Doctor</label>
            <Select options={doctorOptions} value={selectedDoctor} onChange={handleDoctorChange} isClearable placeholder="All Doctors" styles={reactSelectStyles} />
          </div>

          <div className="col" style={{ minWidth: "250px" }}>
            <label className="form-label mb-1">Search</label>
            <div className="search-box">
              <input className="form-control search" name="search_key" onChange={handleChange} value={formData.search_key} placeholder="Search by name, UHID, mobile..." type="text" />
              <i className="ri-search-line search-icon"></i>
            </div>
          </div>

          <div className="col-auto">
            <div className="d-flex gap-2">
              <button type="button" className="btn btn-secondary" onClick={handleReset}><i className="ri-refresh-line me-1"></i> Clear</button>
              <button type="button" className="btn btn-success" onClick={handleExportPDF} disabled={formData.records.length === 0}><i className="ri-file-pdf-line me-1"></i> Export PDF</button>
            </div>
          </div>
        </div>
      </div>

      <div className="card-body pt-3">
        <DataTable
          columns={columns}
          data={formData.records}
          customStyles={customStyles}
          pagination
          highlightOnHover
          dense
          persistTableHead
          progressPending={loading}
          noDataComponent={<div className="text-muted py-3">No records found</div>}
        />

        {formData.records.length > 0 && (
          <div className="d-flex justify-content-end mt-3">
            <div className="text-muted" style={{ fontSize: "0.95rem" }}>
              <strong>Total Amount:</strong> ₹{totals.totalAmount} &nbsp;|&nbsp;
              <strong>Total Paid:</strong> ₹{totals.totalPaid} &nbsp;|&nbsp;
              <strong>Doctor Commission:</strong> ₹{totals.totalCommission} &nbsp;|&nbsp;
              <strong>Doctor Paid:</strong> ₹{totals.totalDocPaid} &nbsp;|&nbsp;
              <strong>Doctor Due:</strong> ₹{totals.totalDocDue}
            </div>
          </div>
        )}
      </div>

      {/* Pay Modal */}
      {payModalOpen && (
        <div className="modal fade show" style={{ display: "block", backgroundColor: "rgba(0,0,0,0.4)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <form onSubmit={handlePaySubmit}>
                <div className="modal-header">
                  <h5 className="modal-title">Pay Doctor Commission</h5>
                  <button type="button" className="btn-close" onClick={closePayModal}></button>
                </div>
                <div className="modal-body">
                  {payRow && (
                    <>
                      <p className="mb-2"><strong>Doctor:</strong> {payRow.doctor_name || payRow.lab_reg_referral || "N/A"}</p>
                      <p className="mb-2"><strong>Patient:</strong> {payRow.lab_reg_name} ({payRow.lab_reg_uhid})</p>
                      <p className="mb-2">
                        <strong>Commission:</strong> ₹{getDoctorNumbers(payRow).commission.toFixed(2)} &nbsp;|&nbsp;
                        <strong>Paid:</strong> ₹{getDoctorNumbers(payRow).paid.toFixed(2)} &nbsp;|&nbsp;
                        <strong>Due:</strong> ₹{getDoctorNumbers(payRow).due.toFixed(2)}
                      </p>

                      <div className="mb-3">
                        <label className="form-label">Pay Amount (₹)</label>
                        <input type="number" className="form-control" step="0.01" min="0" value={payAmount} onChange={(e) => setPayAmount(e.target.value)} />
                      </div>

                      {payError && <div className="alert alert-danger py-2">{payError}</div>}
                    </>
                  )}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-light" onClick={closePayModal} disabled={payProcessing}>Close</button>
                  <button type="submit" className="btn btn-primary" disabled={payProcessing}>{payProcessing ? "Saving..." : "Save Payment"}</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LabReferral;
